import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, IconButton, Divider, Select, Checkbox, Textarea, Image,
} from '@chakra-ui/react';
import { FiEdit2, FiX, FiSave, FiImage } from 'react-icons/fi';
import api from '../api';
import ImageUpload from './ImageUpload';

export default function ProjectDetailModal({ projectId, isOpen, onClose, onRefresh }) {
  const [project, setProject] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [companyResults, setCompanyResults] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetchProject = async () => {
    if (!projectId) return;
    try {
      const { data: projs } = await api.get('/projects');
      const p = projs.find(pr => pr.id === projectId);
      if (p) { setProject(p); setForm({ project_name: p.project_name || '', commissioning_date: p.commissioning_date ? p.commissioning_date.split('T')[0] : '', deadline_date: p.deadline_date ? p.deadline_date.split('T')[0] : '', deadline_type: p.deadline_type || 'flexible', po_required: !!p.po_required, invoicing_notes: p.invoicing_notes || '', logo_file_id: p.logo_file_id || null }); }
    } catch(e) {}
  };

  const fetchCompanies = async () => { try { const { data } = await api.get(`/projects/${projectId}/companies`); setCompanies(data); } catch(e) {} };
  const fetchContacts = async () => { try { const { data } = await api.get(`/projects/${projectId}/contacts`); setContacts(data); } catch(e) {} };
  const fetchUsers = async () => { try { const { data } = await api.get(`/projects/${projectId}/users`); setUsers(data); } catch(e) {} };
  const fetchAllUsers = async () => { try { const { data } = await api.get('/calendar/users'); setAllUsers(data); } catch(e) {} };

  useEffect(() => {
    if (isOpen && projectId) { fetchProject(); fetchCompanies(); fetchContacts(); fetchUsers(); fetchAllUsers(); setEditing(false); }
  }, [isOpen, projectId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}`, form);
      toast({ title: 'Saved', status: 'success', duration: 1500 });
      setEditing(false); fetchProject(); if (onRefresh) onRefresh();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const searchCompanies = async (q) => { if (q.length < 1) { setCompanyResults([]); return; } try { const { data } = await api.get(`/companies/search?q=${encodeURIComponent(q)}&limit=8`); setCompanyResults(data.filter(c => !companies.find(co => co.company_id === c.id))); } catch(e) {} };
  const searchContacts = async (q) => { if (q.length < 1) { setContactResults([]); return; } try { const { data } = await api.get(`/contacts/search?q=${encodeURIComponent(q)}`); setContactResults(data.filter(c => !contacts.find(ct => ct.contact_id === c.id))); } catch(e) {} };

  const addCompany = async (c) => { await api.post(`/projects/${projectId}/companies`, { company_id: c.id }); setCompanySearch(''); setCompanyResults([]); fetchCompanies(); if (onRefresh) onRefresh(); };
  const removeCompany = async (id) => { await api.delete(`/projects/${projectId}/companies/${id}`); fetchCompanies(); if (onRefresh) onRefresh(); };
  const addContact = async (c) => { const ids = [...contacts.map(ct => ct.id || ct.contact_id), c.id]; await api.put(`/projects/${projectId}/contacts`, { contact_ids: ids }); setContactSearch(''); setContactResults([]); fetchContacts(); if (onRefresh) onRefresh(); };
  const removeContact = async (id) => { const ids = contacts.filter(c => (c.id || c.contact_id) !== id).map(c => c.id || c.contact_id); await api.put(`/projects/${projectId}/contacts`, { contact_ids: ids }); fetchContacts(); if (onRefresh) onRefresh(); };
  const addUser = async (uid) => { await api.post(`/projects/${projectId}/users`, { user_id: uid }); fetchUsers(); };
  const removeUser = async (uid) => { await api.delete(`/projects/${projectId}/users/${uid}`); fetchUsers(); };

  const fmtDate = (d) => d ? new Date(d.split('T')[0] + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  if (!project) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex justify="space-between" pr={8}>
            Project Details
            <IconButton icon={editing ? <FiSave /> : <FiEdit2 />} size="xs" variant="ghost"
              onClick={editing ? handleSave : () => setEditing(true)} isLoading={saving} aria-label="Edit" />
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            {/* Project Info */}
            {editing ? (
              <>
                {/* Logo upload */}
                <Box>
                  <FormLabel fontSize="xs" mb={1}>Project Logo</FormLabel>
                  <HStack spacing={3} align="center">
                    {form.logo_file_id && project?.project_logo_url ? (
                      <Box position="relative">
                        <Image src={`/api/files/${project.project_logo_url}`} alt="Project logo"
                          h="52px" maxW="120px" objectFit="contain" borderRadius="md" border="1px" borderColor="gray.200" p={1} />
                        <IconButton icon={<FiX size={10} />} size="xs" colorScheme="red" variant="solid"
                          position="absolute" top="-6px" right="-6px" minW="16px" h="16px" borderRadius="full"
                          onClick={() => { setForm(p => ({ ...p, logo_file_id: null })); api.put(`/projects/${projectId}`, { logo_file_id: null }).then(() => fetchProject()); }}
                          aria-label="Remove logo" />
                      </Box>
                    ) : null}
                    <ImageUpload folder="logos" entityType="project" entityId={projectId}
                      currentFileId={form.logo_file_id}
                      size="52px" label="Logo"
                      onUpdate={(fileId) => {
                        setForm(p => ({ ...p, logo_file_id: fileId }));
                        // Save immediately so fetchProject gets the updated logo_url
                        api.put(`/projects/${projectId}`, { logo_file_id: fileId }).then(() => fetchProject()).catch(() => {});
                      }} />
                    <Box>
                      <Text fontSize="11px" color="gray.500">PNG, JPG or SVG</Text>
                      <Text fontSize="10px" color="gray.400">Replaces the project name in the grid</Text>
                    </Box>
                  </HStack>
                </Box>
                <FormControl><FormLabel fontSize="xs" mb={0.5}>Project Name</FormLabel>
                  <Input size="sm" value={form.project_name} onChange={e => setForm(p => ({ ...p, project_name: e.target.value }))} /></FormControl>
                <HStack spacing={2}>
                  <FormControl><FormLabel fontSize="xs" mb={0.5}>Creation Date</FormLabel>
                    <Input size="sm" type="date" value={form.commissioning_date} onChange={e => setForm(p => ({ ...p, commissioning_date: e.target.value }))} /></FormControl>
                  <FormControl><FormLabel fontSize="xs" mb={0.5}>Deadline</FormLabel>
                    <Input size="sm" type="date" value={form.deadline_date} onChange={e => setForm(p => ({ ...p, deadline_date: e.target.value }))} /></FormControl>
                  <FormControl><FormLabel fontSize="xs" mb={0.5}>Type</FormLabel>
                    <Select size="sm" value={form.deadline_type} onChange={e => setForm(p => ({ ...p, deadline_type: e.target.value }))}>
                      <option value="flexible">Flexible</option><option value="hard">Hard</option></Select></FormControl>
                </HStack>
                <Box bg="cyan.50" p={3} borderRadius="md" border="1px" borderColor="cyan.200" w="100%">
                  <Text fontSize="9px" fontWeight="700" color="cyan.700" textTransform="uppercase" mb={2}>Invoicing Workflow</Text>
                  <VStack spacing={2}>
                    <Checkbox size="sm" isChecked={form.po_required} onChange={e => setForm(p => ({ ...p, po_required: e.target.checked }))} colorScheme="cyan" w="100%">
                      <Text fontSize="12px" fontWeight="600" color="cyan.800">PO Required</Text>
                    </Checkbox>
                    <FormControl>
                      <FormLabel fontSize="xs" mb={0.5}>Invoicing Notes</FormLabel>
                      <Textarea size="sm" rows={2} value={form.invoicing_notes} onChange={e => setForm(p => ({ ...p, invoicing_notes: e.target.value }))}
                        placeholder="e.g. Invoice goes to accounts@client.com, must include PO number..." />
                    </FormControl>
                  </VStack>
                </Box>
              </>
            ) : (
              <>
                {/* Logo display */}
                {project.project_logo_url && (
                  <Box mb={1}>
                    <Image src={`/api/files/${project.project_logo_url}`} alt={project.project_name}
                      h="48px" maxW="160px" objectFit="contain" borderRadius="md" />
                  </Box>
                )}
                <Text fontSize="16px" fontWeight="700">{project.project_name || 'Untitled'}</Text>
                <HStack spacing={4} fontSize="12px" color="gray.500">
                  <Box><Text fontSize="9px" fontWeight="600" color="gray.400" textTransform="uppercase">Created</Text>{fmtDate(project.commissioning_date)}</Box>
                  <Box><Text fontSize="9px" fontWeight="600" color="gray.400" textTransform="uppercase">Deadline</Text>{fmtDate(project.deadline_date)}</Box>
                  {project.deadline_type === 'hard' && <Badge colorScheme="red" fontSize="9px">Hard Deadline</Badge>}
                  <Box><Text fontSize="9px" fontWeight="600" color="gray.400" textTransform="uppercase">Priority</Text>{project.priority_level ?? 5}/10</Box>
                </HStack>
                {(project.po_required || project.invoicing_notes) && (
                  <Box bg="cyan.50" p={2} borderRadius="md" border="1px" borderColor="cyan.200" w="100%">
                    <Text fontSize="9px" fontWeight="700" color="cyan.700" textTransform="uppercase" mb={1}>Invoicing</Text>
                    {project.po_required && <Badge colorScheme="cyan" fontSize="9px" mb={1}>PO Required</Badge>}
                    {project.invoicing_notes && <Text fontSize="11px" color="gray.700">{project.invoicing_notes}</Text>}
                  </Box>
                )}
              </>
            )}

            {/* Companies */}
            <Divider />
            <Box>
              <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" mb={1}>Companies</Text>
              <Flex gap={1} flexWrap="wrap" mb={1}>
                {companies.map(c => (
                  <Badge key={c.company_id} colorScheme="blue" fontSize="10px" display="flex" alignItems="center" gap={1}>
                    {c.name}
                    <IconButton icon={<FiX />} size="xs" variant="ghost" minW="14px" h="14px" onClick={() => removeCompany(c.company_id)} aria-label="Remove" />
                  </Badge>
                ))}
                {companies.length === 0 && <Text fontSize="11px" color="gray.400">None</Text>}
              </Flex>
              <Input size="xs" placeholder="Search company to add..." value={companySearch}
                onChange={e => { setCompanySearch(e.target.value); searchCompanies(e.target.value); }} />
              {companyResults.length > 0 && (
                <Box border="1px" borderColor="gray.200" borderRadius="md" mt={1} maxH="100px" overflowY="auto" bg="white">
                  {companyResults.map(c => (
                    <Box key={c.id} px={2} py={1} fontSize="11px" cursor="pointer" _hover={{ bg: 'blue.50' }} onClick={() => addCompany(c)}>{c.name}</Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Contacts */}
            <Box>
              <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" mb={1}>Contacts</Text>
              <Flex gap={1} flexWrap="wrap" mb={1}>
                {contacts.map(c => (
                  <Badge key={c.id || c.contact_id} colorScheme="purple" fontSize="10px" display="flex" alignItems="center" gap={1}>
                    {c.first_name} {c.last_name || ''}
                    <IconButton icon={<FiX />} size="xs" variant="ghost" minW="14px" h="14px" onClick={() => removeContact(c.id || c.contact_id)} aria-label="Remove" />
                  </Badge>
                ))}
                {contacts.length === 0 && <Text fontSize="11px" color="gray.400">None</Text>}
              </Flex>
              <Input size="xs" placeholder="Search contact to add..." value={contactSearch}
                onChange={e => { setContactSearch(e.target.value); searchContacts(e.target.value); }} />
              {contactResults.length > 0 && (
                <Box border="1px" borderColor="gray.200" borderRadius="md" mt={1} maxH="100px" overflowY="auto" bg="white">
                  {contactResults.map(c => (
                    <Box key={c.id} px={2} py={1} fontSize="11px" cursor="pointer" _hover={{ bg: 'purple.50' }} onClick={() => addContact(c)}>
                      {c.first_name} {c.last_name || ''} {c.company_name ? `(${c.company_name})` : ''}</Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Users */}
            <Box>
              <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" mb={1}>Assigned Users</Text>
              <Flex gap={1} flexWrap="wrap" mb={1}>
                {users.map(u => (
                  <Badge key={u.user_id} colorScheme="teal" fontSize="10px" display="flex" alignItems="center" gap={1}>
                    {u.display_name || u.username}
                    <IconButton icon={<FiX />} size="xs" variant="ghost" minW="14px" h="14px" onClick={() => removeUser(u.user_id)} aria-label="Remove" />
                  </Badge>
                ))}
                {users.length === 0 && <Text fontSize="11px" color="gray.400">None</Text>}
              </Flex>
              <Select size="xs" placeholder="Add user..." value=""
                onChange={async (e) => { if (e.target.value) { await addUser(parseInt(e.target.value)); } }}>
                {allUsers.filter(u => !users.find(pu => pu.user_id === u.id)).map(u => (
                  <option key={u.id} value={u.id}>{u.display_name || u.username}</option>
                ))}
              </Select>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}
